import { Feature, Feature as GeoFeature, LineString, MultiPolygon, Polygon } from "geojson"
import { IBoundaryBox, ILocation, IPoint } from "geometry/model"
import { getTurf } from "geometry/service"

export function getFeature(feature: GeoFeature | ILocation | IPoint, properties?: any): GeoFeature {
  if (Array.isArray(feature)) {
    return getTurf().point(feature, properties)
  } else if (feature.hasOwnProperty("x")) {
    return getTurf().point([(feature as ILocation).x, (feature as ILocation).y], properties)
  } else {
    const feat = feature as GeoFeature
    if (properties) {
      feat.properties = properties
    }
    return feat
  }
}

export function getPointFromLocation(location: IPoint | ILocation): IPoint {
  return Array.isArray(location) ? location : [location.x, location.y]
}

export function areCoordinatesEquals(coord1: IPoint, coord2: IPoint): boolean {
  if (coord1.length !== coord2.length) {
    throw new Error("Cannot compare coordinates of different dimension")
  }
  if (coord1.length === 2) {
    return coord1[0] === coord2[0] && coord1[1] === coord2[1]
  } else {
    return coord1[0] === coord2[0] && coord1[1] === coord2[1] && coord1[2] === coord2[2]
  }
}

export function getIBoundaryBox(
  bbox: [number, number, number, number] | [number, number, number, number, number, number]
): IBoundaryBox {
  return {
    sw: { x: bbox[0], y: bbox[1] },
    ne: { x: bbox[2], y: bbox[3] }
  }
}

export function getUniqueFeatures(featuresWithDuplicate: Feature[]): Feature[] {
  const features: Feature[] = []
  const featureIds: number[] = []
  for (const feature of featuresWithDuplicate) {
    if (!featureIds.includes(feature.id as number)) {
      features.push(feature)
      featureIds.push(feature.id as number)
    }
  }
  return features
}

function geometryIntersect(turf: any, f1: Feature, f2: Feature): boolean {
  try {
    // we are waiting for the next release of turf (6.2.0) to use the booleanIntersects() method:
    // https://github.com/Turfjs/turf/tags
    // the features either intersects, or the f1 feature (the "intersector") is a polygon and contains the f2 feature
    return (f1.geometry.type === "Polygon" && turf.booleanContains(f1, f2)) || !turf.booleanDisjoint(f1, f2)
  } catch (ex) {
    console.error("geometryIntersect error", ex, f1, f2)
  }
  return false
}

export function featureIntersect(turf: any, f1: Feature, f2: Feature): boolean {
  if (!f1) {
    throw Error("featureIntersect : Invalid feature1 parameter")
  }
  if (!f1.geometry) {
    throw Error("featureIntersect : Invalid feature1 geometry")
  }
  if (!f2) {
    throw Error("featureIntersect : Invalid feature2 parameter")
  }
  if (!f2.geometry) {
    throw Error("featureIntersect : Invalid feature2 geometry")
  }
  try {
    switch (f2.geometry.type) {
      case "Point":
      case "Polygon":
      case "LineString": {
        return geometryIntersect(turf, f1, f2)
      }
      case "MultiLineString": {
        const coordinates: IPoint[][][] = (f2.geometry as any).coordinates as IPoint[][][]
        const lineStrings: Array<Feature<LineString> | null> = coordinates.map(c => {
          try {
            return turf.lineString(c)
          } catch (ex) {
            console.warn("featureIntersect : some lineString segments have been ignored", ex)
            return null
          }
        })
        for (const lineString of lineStrings) {
          if (lineString !== null) {
            if (geometryIntersect(turf, f1, lineString)) {
              return true
            }
          }
        }
        return false
      }
      case "MultiPolygon": {
        const coordinates: IPoint[][][] = (f2.geometry as any).coordinates as IPoint[][][]
        const polygons: Array<Feature<Polygon> | null> = coordinates.map(c => {
          try {
            if (c[0][0][0] === undefined) {
              c = [c as any]
            }
            return turf.polygon(c)
          } catch (ex) {
            console.warn("featureIntersect : some polygon has been ignored", ex)
            return null
          }
        })
        for (const polygon of polygons) {
          if (polygon !== null) {
            if (geometryIntersect(turf, f1, polygon)) {
              return true
            }
          }
        }
        return false
      }
      default:
        return turf.booleanCrosses(f1, f2)
    }
  } catch (error) {
    console.error(error)
    return false
  }
}

export function areLocationsEqual(l1: ILocation | undefined, l2: ILocation | undefined) {
  if (l1 === undefined) {
    return l2 === undefined
  } else if (l2 === undefined) {
    return false
  }
  return l1.x === l2.x && l1.y === l2.y
}

export function getMultiPolygonIntersection(
  feature1: Feature<MultiPolygon>,
  feature2: Feature<MultiPolygon>
): Feature<MultiPolygon> | null {
  return getTurf().intersect(feature1, feature2)
}
